import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useVisibleSections, type VisibleSection } from "../../hooks/useVisibleSections.js";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion.js";
import { isProfileRoute, PROFILE_PATH } from "../../lib/profileTabs.js";
import { type NavLinkItem, type NavGroup } from "../../nav/navConfig.js";
import { cn } from "../../utils/cn.js";
import { trackEvent } from "../../lib/auditCollector.js";
import { Badge } from "../ui/Badge.js";
import { Icon } from "../ui/Icon.js";
import { Logo } from "./Logo.js";
import { AppStatusDot } from "../system/AppStatusDot.js";

type MobileNavDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const trapFocus = (container: HTMLElement, event: ReactKeyboardEvent<HTMLElement> | KeyboardEvent) => {
  if (event.key !== "Tab") return;
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  if (focusable.length === 0) return;

  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

export const MobileNavDrawer = ({ open, onClose }: MobileNavDrawerProps) => {
  const {
    visibleSections,
    fallbackSection,
    isAdmin,
    isNavItemActive,
    hasUpdatesAvailable,
    installedSoftwareIds,
    quickNavItems
  } = useVisibleSections();
  const location = useLocation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Track which sections are expanded (accordion)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  // Track which groups within sections are expanded
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Track previous open state for animation
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  // Store the pathname that was active when drawer last opened
  const openPathRef = useRef(location.pathname);

  // Auto-expand the section containing the active route
  useEffect(() => {
    if (!open) return;
    openPathRef.current = location.pathname;

    const activeSections = new Set<string>();
    const activeGroups = new Set<string>();

    for (const section of visibleSections) {
      for (const item of section.items) {
        if (isNavItemActive(item, location.pathname, location.search)) {
          activeSections.add(section.id);
        }
      }
      // Recursively check groups and nested child groups
      const checkGroups = (groups: NavGroup[]) => {
        for (const group of groups) {
          let groupIsActive = false;
          if (group.path && (location.pathname === group.path || location.pathname.startsWith(`${group.path}/`))) {
            groupIsActive = true;
          }
          for (const item of group.items) {
            if (isNavItemActive(item, location.pathname, location.search)) {
              groupIsActive = true;
            }
          }
          if (group.groups) {
            // Check nested children — if any child is active, this parent should also expand
            const beforeSize = activeGroups.size;
            checkGroups(group.groups);
            if (activeGroups.size > beforeSize) {
              groupIsActive = true;
            }
          }
          if (groupIsActive) {
            activeSections.add(section.id);
            activeGroups.add(`${section.id}::${group.id}`);
          }
        }
      };
      if (section.groups) {
        checkGroups(section.groups);
      }
    }

    // Check fallback (Account) section
    if (isProfileRoute(location.pathname)) {
      activeSections.add(fallbackSection.id);
    }

    setExpandedSections(activeSections);
    setExpandedGroups(activeGroups);
  }, [open]);

  // Animate open/close
  useEffect(() => {
    if (open) {
      setVisible(true);
      // Trigger animation on next frame
      requestAnimationFrame(() => setAnimating(true));
    } else {
      setAnimating(false);
      if (prefersReducedMotion) {
        setVisible(false);
      } else {
        const timer = window.setTimeout(() => setVisible(false), 200);
        return () => window.clearTimeout(timer);
      }
    }
  }, [open, prefersReducedMotion]);

  // Focus close button when drawer opens
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 10);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Escape key closes drawer
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Close on route change
  useEffect(() => {
    if (!open) return;
    if (location.pathname !== openPathRef.current) {
      onClose();
    }
  }, [location.pathname, open, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Return focus to hamburger on close
  const returnFocusOnClose = useCallback(() => {
    onClose();
    requestAnimationFrame(() => {
      const toggle = document.querySelector<HTMLElement>(".nav-toggle");
      toggle?.focus();
    });
  }, [onClose]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const toggleGroup = (sectionId: string, groupId: string) => {
    const key = `${sectionId}::${groupId}`;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleLinkClick = (itemId?: string) => {
    trackEvent({ category: "ui", action: "mobile-nav.link-click", component: "MobileNavDrawer", details: itemId ? { itemId } : undefined });
  };

  /** Timeline-style item for sub-sections/sub-pages (dot + label, pulsing active dot). */
  const renderTimelineItem = (item: NavLinkItem) => {
    if (item.disabled) return null;

    const hashIdx = item.path.indexOf("#");
    const isActive = hashIdx !== -1
      ? location.hash === item.path.slice(hashIdx)
        && location.pathname === item.path.slice(0, hashIdx)
      : location.pathname === item.path;

    return (
      <NavLink
        key={item.id}
        to={item.path}
        className={cn(
          "group/tl relative flex min-h-[40px] items-center gap-2 rounded-md py-2 pl-3 pr-3 text-xs transition-colors duration-150",
          isActive
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
        onClick={(e) => {
          if (hashIdx !== -1) {
            const hash = item.path.split("#")[1];
            const itemPathname = item.path.split("#")[0];
            if (hash && location.pathname === itemPathname) {
              e.preventDefault();
              window.location.hash = hash;
              const el = document.getElementById(hash);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }
          }
          handleLinkClick(item.id);
        }}
      >
        <span className="absolute -left-[3px] top-1/2 -translate-y-1/2">
          {isActive && (
            <span className="absolute inset-[-3px] animate-ping rounded-full bg-primary/30" />
          )}
          <span
            className={cn(
              "block h-1.5 w-1.5 rounded-full transition-colors duration-150",
              isActive
                ? "bg-primary shadow-[0_0_6px_1px] shadow-primary/40"
                : "bg-muted-foreground/40 group-hover/tl:bg-primary/60"
            )}
          />
        </span>
        <span className="truncate">{item.label}</span>
      </NavLink>
    );
  };

  const renderNavItem = (item: NavLinkItem, sectionId: string) => {
    if (item.disabled) return null;

    const isActive = isNavItemActive(item, location.pathname, location.search);
    const shouldShowUpdateBadge = item.id === "system-changelog" && isAdmin && hasUpdatesAvailable;
    const isHashLink = item.path.includes("#");

    const quickNavItem = quickNavItems.find((qi) => qi.id === item.id);
    const showStatusDot =
      sectionId === "software" &&
      installedSoftwareIds.has(item.id) &&
      quickNavItem?.showServiceStatus !== false;

    const content = (
      <>
        {item.icon && <Icon icon={item.icon} className="h-4 w-4 flex-shrink-0" />}
        <span className="flex-1 truncate">{item.label}</span>
        {showStatusDot && (
          <AppStatusDot appId={item.id} appLabel={item.label} appPath={item.path} />
        )}
        {shouldShowUpdateBadge && (
          <Badge variant="primary" size="xs" className="animate-pulse">
            Update
          </Badge>
        )}
      </>
    );

    if (item.external) {
      return (
        <a
          key={item.id}
          href={item.path}
          target="_blank"
          rel="noreferrer"
          className={cn(
            "flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
            "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          )}
          onClick={() => handleLinkClick(item.id)}
        >
          {content}
          <Icon icon="lucide:arrow-up-right" className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="sr-only">(opens in new tab)</span>
        </a>
      );
    }

    return (
      <NavLink
        key={item.id}
        to={item.path}
        className={cn(
          "flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          isActive
            ? "bg-primary/10 text-foreground font-medium"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        )}
        onClick={(e) => {
          // For hash links on the same page, React Router won't trigger
          // browser hash-scrolling.  Set the hash directly and scroll.
          if (isHashLink) {
            const hash = item.path.split("#")[1];
            const itemPathname = item.path.split("#")[0];
            if (hash && location.pathname === itemPathname) {
              e.preventDefault();
              window.location.hash = hash;
              const el = document.getElementById(hash);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }
          }
          handleLinkClick(item.id);
        }}
      >
        {content}
        {!isHashLink && !showStatusDot && !shouldShowUpdateBadge && (
          <Icon icon="lucide:chevron-right" className="h-3.5 w-3.5 text-muted-foreground/50" />
        )}
      </NavLink>
    );
  };

  const renderGroup = (group: NavGroup, sectionId: string) => {
    const key = `${sectionId}::${group.id}`;
    const isExpanded = expandedGroups.has(key);
    const contentId = `mobile-nav-group-${group.id}`;

    // Dual-action for navigable groups: label click navigates, chevron toggles
    if (group.path) {
      const isActive = location.pathname === group.path || location.pathname.startsWith(`${group.path}/`);
      return (
        <li key={group.id}>
          <div className="flex items-center">
            <NavLink
              to={group.path}
              className={cn(
                "flex min-h-[40px] flex-1 items-center gap-3 rounded-l-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors",
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              )}
              onClick={() => handleLinkClick(group.id)}
            >
              {group.icon && <Icon icon={group.icon} className="h-3.5 w-3.5" />}
              <span className="flex-1 text-left truncate">{group.label}</span>
            </NavLink>
            <button
              type="button"
              onClick={() => toggleGroup(sectionId, group.id)}
              className="flex min-h-[40px] items-center rounded-r-md px-2 py-1.5 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
              aria-expanded={isExpanded}
              aria-controls={contentId}
              aria-label={`Toggle ${group.label} sub-navigation`}
            >
              <Icon
                icon="lucide:chevron-down"
                className={cn("h-3.5 w-3.5 transition-transform duration-150", isExpanded && "rotate-180")}
              />
            </button>
          </div>
          {isExpanded && (
            <ul id={contentId} className="ml-3 space-y-0.5 border-l border-border/40 pl-3 pb-1">
              {group.items.map((item) => {
                const rendered = renderTimelineItem(item);
                return rendered ? <li key={item.id}>{rendered}</li> : null;
              })}
              {group.groups?.map((childGroup) => renderGroup(childGroup, sectionId))}
            </ul>
          )}
        </li>
      );
    }

    // Non-navigable group header (original behavior)
    return (
      <li key={group.id}>
        <button
          type="button"
          onClick={() => toggleGroup(sectionId, group.id)}
          className="flex min-h-[40px] w-full items-center gap-3 rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          {group.icon && <Icon icon={group.icon} className="h-3.5 w-3.5" />}
          <span className="flex-1 text-left truncate">{group.label}</span>
          <Icon
            icon="lucide:chevron-down"
            className={cn("h-3.5 w-3.5 transition-transform duration-150", isExpanded && "rotate-180")}
          />
        </button>
        {isExpanded && (
          <ul id={contentId} className="ml-3 space-y-0.5 border-l border-border/40 pl-3 pb-1">
            {group.items.map((item) => {
              const rendered = renderNavItem(item, sectionId);
              return rendered ? <li key={item.id}>{rendered}</li> : null;
            })}
            {group.groups?.map((childGroup) => renderGroup(childGroup, sectionId))}
          </ul>
        )}
      </li>
    );
  };

  const renderSection = (section: VisibleSection) => {
    const isExpanded = expandedSections.has(section.id);
    const contentId = `mobile-nav-section-${section.id}`;

    // Determine if the section has groups (like Software or Dashboard with streaming)
    const hasGroups = section.groups && section.groups.length > 0;

    // Flat items that don't belong to any group
    const groupItemIds = new Set(
      section.groups?.flatMap((g) => g.items.map((i) => i.id)) ?? []
    );
    const flatItems = section.items.filter((item) => !groupItemIds.has(item.id));

    return (
      <li key={section.id}>
        <button
          type="button"
          onClick={() => toggleSection(section.id)}
          className="flex min-h-[48px] w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted/40"
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          {section.icon && <Icon icon={section.icon} className="h-5 w-5" />}
          <span className="flex-1 text-left">{section.label}</span>
          <Icon
            icon="lucide:chevron-down"
            className={cn("h-4 w-4 text-muted-foreground transition-transform duration-150", isExpanded && "rotate-180")}
          />
        </button>
        {isExpanded && (
          <ul id={contentId} className="space-y-0.5 pb-2 pl-2">
            {/* Render flat items first */}
            {flatItems.map((item) => {
              const rendered = renderNavItem(item, section.id);
              return rendered ? <li key={item.id}>{rendered}</li> : null;
            })}
            {/* Then render groups */}
            {hasGroups && section.groups!.map((group) => renderGroup(group, section.id))}
          </ul>
        )}
      </li>
    );
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="presentation">
      {/* Backdrop */}
      <button
        type="button"
        className={cn(
          "absolute inset-0 bg-black/50 transition-opacity",
          prefersReducedMotion ? "" : "duration-200",
          animating ? "opacity-100" : "opacity-0"
        )}
        aria-label="Close navigation"
        onClick={returnFocusOnClose}
        tabIndex={-1}
      />

      {/* Drawer panel */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "absolute left-0 top-0 flex h-full w-[min(85vw,320px)] flex-col border-r border-border/60 bg-card/95 shadow-2xl backdrop-blur-xl",
          prefersReducedMotion
            ? ""
            : "transition-transform duration-200 ease-out",
          animating ? "translate-x-0" : "-translate-x-full"
        )}
        onKeyDown={(event) => {
          if (panelRef.current) {
            trapFocus(panelRef.current, event);
          }
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
          <div className="flex items-center gap-3" id={titleId}>
            <Logo collapsed />
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={returnFocusOnClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            aria-label="Close navigation"
          >
            <Icon icon="lucide:x" className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overscroll-contain qb-scroll px-3 py-3" aria-label="Main navigation">
          <ul className="space-y-1">
            {visibleSections.map((section) => renderSection(section))}
            {/* Always show the Account/fallback section */}
            {renderSection(fallbackSection)}
          </ul>
        </nav>
      </aside>
    </div>
  );
};
